import React from 'react';
import Select, { components, MenuProps, SingleValue, StylesConfig } from 'react-select';
import { Sector } from '../../features/admin/companies/types';

interface SectorOption {
  value: number;
  label: string;
}

interface Props {
  value: number | null | undefined;
  sectors: Sector[];
  onChange: (value: number | null) => void;
  onAddSector?: () => void;
  borderColor?: string;
  isDisabled?: boolean;
  placeholder?: string;
}

export const SectorSelect: React.FC<Props> = ({
  value,
  sectors,
  onChange,
  onAddSector,
  borderColor = '#E5E7EB',
  isDisabled = false,
  placeholder = 'Select a sector...',
}) => {
  const options: SectorOption[] = sectors.map((s) => ({ value: s.id, label: s.name }));
  const selected = options.find((o) => o.value === value) || null;

  const handleChange = (option: SingleValue<SectorOption>) => {
    onChange(option ? option.value : null);
  };

  const styles: StylesConfig<SectorOption, false> = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#3B82F6' : borderColor,
      borderRadius: '0.5rem',
      padding: '2px 4px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.3)' : 'none',
      '&:hover': { borderColor: state.isFocused ? '#3B82F6' : borderColor },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.5rem',
      zIndex: 50,
      overflow: 'hidden',
    }),
  };

  const MenuWithAdd = (menuProps: MenuProps<SectorOption, false>) => (
    <components.Menu {...menuProps}>
      <button
        type="button"
        onMouseDown={(e) => {
          // Prevent react-select from treating this as an option click / blur
          e.preventDefault();
          e.stopPropagation();
          onAddSector?.();
        }}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium border-b transition-colors hover:bg-blue-50"
        style={{ color: '#2563EB', borderColor: '#E5E7EB' }}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
        Add new sector
      </button>
      {menuProps.children}
    </components.Menu>
  );

  return (
    <Select<SectorOption>
      options={options}
      value={selected}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable
      isSearchable
      isDisabled={isDisabled}
      styles={styles}
      components={onAddSector ? { Menu: MenuWithAdd } : undefined}
    />
  );
};
